import React from 'react';
import { Card, CardContent, Typography, Grid } from '@mui/material';
import Chart from 'react-apexcharts';
import { LoadingWrapper } from '../loading';
import useChart from '../chart/useChart';

const FinanceCharts = ({ monthlyData, expenseBreakdown, revenueBreakdown, cashFlowData, isLoading }) => {
  const revenueExpensesOptions = useChart({
    chart: {
      type: 'line',
      height: 350,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    colors: ['#0A9EF2', '#5E69F4', '#4CAF50'],
    xaxis: {
      categories: monthlyData.months,
      title: {
        text: 'Month',
      },
    },
    yaxis: {
      title: {
        text: 'Amount ($)',
      },
      labels: {
        formatter: (val) => `$${(val / 1000).toFixed(0)}K`,
      },
    },
    legend: {
      position: 'top',
    },
    tooltip: {
      y: {
        formatter: (val) => `$${val.toLocaleString()}`,
      },
    },
    title: {
      text: 'Revenue vs Expenses Trend',
      align: 'left',
    },
  });

  const revenueExpensesSeries = [
    {
      name: 'Revenue',
      data: monthlyData.revenue,
    },
    {
      name: 'Expenses',
      data: monthlyData.expenses,
    },
    {
      name: 'Net Profit',
      data: monthlyData.profit,
    },
  ];

  // Expense Breakdown Pie Chart
  const expenseBreakdownOptions = useChart({
    chart: {
      type: 'donut',
      height: 350,
    },
    colors: ['#0A9EF2', '#5E69F4', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#00BCD4'],
    labels: expenseBreakdown.map((item) => item.category),
    legend: {
      position: 'bottom',
    },
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val) =>
          `$${val.toLocaleString()} (${(
            (val / expenseBreakdown.reduce((sum, item) => sum + item.amount, 0)) *
            100
          ).toFixed(1)}%)`,
      },
    },
    title: {
      text: 'Expense Breakdown',
      align: 'left',
    },
  });

  const expenseBreakdownSeries = expenseBreakdown.map((item) => item.amount);

  // Revenue Sources Pie Chart
  const revenueBreakdownOptions = useChart({
    chart: {
      type: 'donut',
      height: 350,
    },
    colors: ['#0A9EF2', '#5E69F4', '#4CAF50', '#FF9800'],
    labels: revenueBreakdown.map((item) => item.source),
    legend: {
      position: 'bottom',
    },
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val) =>
          `$${val.toLocaleString()} (${(
            (val / revenueBreakdown.reduce((sum, item) => sum + item.amount, 0)) *
            100
          ).toFixed(1)}%)`,
      },
    },
    title: {
      text: 'Revenue Sources',
      align: 'left',
    },
  });

  const revenueBreakdownSeries = revenueBreakdown.map((item) => item.amount);

  // Cash Flow Bar Chart
  const cashFlowOptions = useChart({
    chart: {
      type: 'bar',
      height: 350,
    },
    colors: ['#0A9EF2', '#5E69F4', '#4CAF50'],
    xaxis: {
      categories: cashFlowData.map((item) => item.month),
      title: {
        text: 'Month',
      },
    },
    yaxis: {
      title: {
        text: 'Amount ($)',
      },
      labels: {
        formatter: (val) => `$${(val / 1000).toFixed(0)}K`,
      },
    },
    legend: {
      position: 'top',
    },
    tooltip: {
      y: {
        formatter: (val) => `$${val.toLocaleString()}`,
      },
    },
    title: {
      text: 'Monthly Cash Flow',
      align: 'left',
    },
  });

  const cashFlowSeries = [
    {
      name: 'Cash Inflow',
      data: cashFlowData.map((item) => item.inflow),
    },
    {
      name: 'Cash Outflow',
      data: cashFlowData.map((item) => item.outflow),
    },
    {
      name: 'Net Cash Flow',
      data: cashFlowData.map((item) => item.net),
    },
  ];

  return (
    <LoadingWrapper
      isLoading={isLoading}
      loadingType="skeleton"
      skeletonCount={4}
      gridProps={{ xs: 12, md: 6 }}
      containerSx={{ marginTop: 3 }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue vs Expenses Trend
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Monthly performance overview showing revenue, expenses, and net profit
              </Typography>
              <Chart options={revenueExpensesOptions} series={revenueExpensesSeries} type="line" height={350} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Cash Flow
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Cash inflow, outflow, and net flow by month
              </Typography>
              <Chart options={cashFlowOptions} series={cashFlowSeries} type="bar" height={350} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Expense Breakdown
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Distribution of expenses by category
              </Typography>
              <Chart options={expenseBreakdownOptions} series={expenseBreakdownSeries} type="donut" height={350} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue Sources
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Revenue distribution by source
              </Typography>
              <Chart options={revenueBreakdownOptions} series={revenueBreakdownSeries} type="donut" height={350} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default FinanceCharts;
